import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {BackendResponse} from '@common/http/backend-response/backend-response';
import {useParams} from 'react-router-dom';
import {
  Album,
  getAlbumRouteAlbumId,
  getDerivedAlbumId,
} from '@app/web-player/albums/album';
import {assignAlbumToTracks} from '@app/web-player/albums/assign-album-to-tracks';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

export interface GetAlbumResponse extends BackendResponse {
  album: Album;
  loader: Params['loader'];
}

interface Params {
  loader: 'albumPage' | 'editAlbumPage' | 'album' | 'albumEmbed';
  albumId?: number;
  derivedId?: number;
}

export function useAlbum(params: Params) {
  const {albumId: albumIdFromParams, derivedId: derivedIdFromParams} =
    useParams();
  const albumId = params.albumId ?? albumIdFromParams;
  const derivedId = params.derivedId ?? derivedIdFromParams;
  return useQuery({
    queryKey: ['albums', albumId ?? 'none', derivedId ?? 'none', params.loader],
    queryFn: () => fetchAlbum(albumId!, derivedId, params),
    enabled: !!albumId,
    initialData: () => {
      if (!albumId) {
        return undefined;
      }

      const data = getBootstrapData().loaders?.[params.loader];
      if (
        data?.loader === params.loader &&
        getAlbumRouteAlbumId(data.album) == albumId &&
        (derivedId
          ? getDerivedAlbumId(data.album) == derivedId
          : !getDerivedAlbumId(data.album))
      ) {
        return data;
      }
      return undefined;
    },
  });
}

function fetchAlbum(
  albumId: number | string,
  derivedId: number | string | undefined,
  params: Params,
) {
  return apiClient
    .get<GetAlbumResponse>(`albums/${albumId}`, {
      params: {
        loader: params.loader,
        ...(derivedId ? {derivedId} : {}),
      },
    })
    .then(response => {
      response.data.album = assignAlbumToTracks(response.data.album);
      return response.data;
    });
}
